import { useEffect, useState } from "react";
import { Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "./AuthContext";
import { OffsiteStatus } from "./OffsiteStatus";

const inputCls = "mt-1 h-12 w-full rounded-xl border border-white/10 bg-black/40 px-4 font-mono tracking-[0.3em] text-stone-100 outline-none focus:border-orange-500";

export const RecoveryCodes = ({ codes, onDone }) => {
  const [saved, setSaved] = useState(false);
  return (
    <div className="rounded-2xl border border-amber-400/40 bg-amber-400/10 p-5 text-sm text-amber-50" data-testid="mfa-recovery-codes">
      <p className="font-semibold">Save your recovery codes</p>
      <p className="mt-1 text-xs text-amber-100">Each code works once if you lose your phone. Store them in a password manager or a sealed envelope. They won't be shown again.</p>
      <ul className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">{codes.map((c) => <li key={c} className="rounded-lg bg-black/50 px-3 py-1.5">{c}</li>)}</ul>
      <button type="button" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Codes copied"); }} className="mt-3 inline-flex h-9 items-center gap-1.5 rounded-full border border-white/20 px-4 text-xs"><Copy className="h-3.5 w-3.5" /> Copy all</button>
      <label className="mt-4 flex items-center gap-2 text-xs"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="h-4 w-4 accent-orange-500" data-testid="mfa-codes-saved" /> I have stored these codes safely</label>
      <button type="button" disabled={!saved} onClick={onDone} className="tc-btn-primary mt-4 h-11 rounded-full px-6 text-sm font-semibold disabled:opacity-40" data-testid="mfa-codes-done">Continue to the dashboard</button>
    </div>
  );
};

export const MfaEnrol = () => {
  const { user, setUser, logout } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    api.post("/auth/mfa/setup").then((r) => { if (alive) setSetup(r.data ?? null); }).catch((err) => setError(formatApiError(err)));
    return () => { alive = false; };
  }, []);

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { const { data } = await api.post("/auth/mfa/enable", { code }); setResult(data); }
    catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#0C0A09] px-5 py-10 text-stone-100" data-testid="mfa-enrol">
      <div className="w-full max-w-md">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-orange-400"><ShieldCheck className="h-4 w-4" /> Required for {user?.role_label}</p>
        <h1 className="mt-3 font-display text-2xl">Set up two-step verification</h1>
        {result ? (
          <div className="mt-6"><RecoveryCodes codes={result.recovery_codes ?? []} onDone={() => setUser(result.me)} /></div>
        ) : (
          <form onSubmit={enable} className="mt-6 space-y-4 rounded-2xl border border-white/10 bg-[#141110] p-5">
            <ol className="list-decimal space-y-1 pl-5 text-sm text-stone-300">
              <li>Open an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password or similar).</li>
              <li>Scan this code, or enter the setup key manually.</li>
              <li>Type the 6-digit code the app shows.</li>
            </ol>
            {setup ? (
              <div className="flex flex-col items-center gap-3">
                <img src={setup.qr} alt="QR code for your authenticator app" className="h-48 w-48 rounded-xl bg-white p-2" data-testid="mfa-qr" />
                <code className="break-all rounded-lg bg-black/60 px-3 py-1.5 text-center font-mono text-xs text-stone-300" data-testid="mfa-secret">{setup.secret}</code>
              </div>
            ) : !error && <Loader2 className="mx-auto h-6 w-6 animate-spin text-orange-400" />}
            <label className="block text-xs text-stone-400">6-digit code
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={8} className={inputCls} data-testid="mfa-enrol-code" />
            </label>
            {error && <p role="alert" className="text-sm text-red-300" data-testid="mfa-enrol-error">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={busy || !setup} className="tc-btn-primary inline-flex h-11 items-center gap-2 rounded-full px-6 text-sm font-semibold" data-testid="mfa-enrol-submit">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Turn on
              </button>
              <button type="button" onClick={logout} className="h-11 rounded-full border border-white/15 px-5 text-sm text-stone-300" data-testid="mfa-enrol-signout">Sign out</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export const Backups = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [report, setReport] = useState(null);
  const load = async () => { try { const r = await api.get("/admin/backups"); setD(r.data ?? null); } catch (err) { toast.error(formatApiError(err)); } };
  useEffect(() => { load(); }, []);
  const run = async (key, fn) => { setBusy(key); try { await fn(); await load(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); } };
  const download = async (file) => {
    const r = await api.get(`/admin/backups/${file}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = file; a.click(); URL.revokeObjectURL(url);
  };
  if (!d) return <p className="mt-6 text-sm text-stone-400">Loading backups…</p>;
  const last = report ?? d.last_restore_test;
  return (
    <div className="mt-6 space-y-4" data-testid="admin-backups">
      <p className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-100" data-testid="backups-interim-warning">
        Local backups: encrypted and created nightly at 01:00 Nairobi time, the newest {d.keep} are kept on this hosting environment. {d.offsite?.active ? "Each one is also copied to Backblaze B2 and verified." : <>Off-site copies are <strong>not</strong> active yet, so download copies to your own secure storage.</>}
      </p>
      <p className="text-xs text-stone-400" data-testid="backups-key-rotation">Encryption keys: current key for new backups{d.previous_keys > 0 ? `, plus ${d.previous_keys} previous key(s) kept for older backups` : "; no previous keys configured"}.</p>
      {d.offsite && <OffsiteStatus o={d.offsite} newest={d.files?.[0]?.file} reload={load} />}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" disabled={!!busy} onClick={() => run("new", async () => { await api.post("/admin/backups"); toast.success("Backup created"); })}
          className="tc-btn-primary inline-flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold" data-testid="backup-run-btn">{busy === "new" && <Loader2 className="h-4 w-4 animate-spin" />} Back up now</button>
        {last && <span className={`text-sm ${last.success ? "text-emerald-300" : "text-red-300"}`} data-testid="backup-last-restore">
          Last restore test {last.at?.slice(0, 16).replace("T", " ")}: {last.success ? "passed" : "FAILED"} ({last.file})</span>}
      </div>
      <ul className="overflow-hidden rounded-2xl border border-white/10" data-testid="backup-list">
        {d.files.length === 0 && <li className="p-6 text-sm text-stone-400">No backups yet.</li>}
        {d.files.map((f) => (
          <li key={f.file} className="flex flex-wrap items-center gap-3 border-b border-white/10 px-5 py-3 text-sm text-stone-200">
            <span className="min-w-0 flex-1 font-mono text-xs">{f.file} · {(f.size_bytes / 1024).toFixed(1)} KB</span>
            <button type="button" disabled={!!busy} onClick={() => run(f.file, async () => { const r = await api.post(`/admin/backups/${f.file}/restore-test`); setReport(r.data); toast[r.data?.success ? "success" : "error"](r.data?.success ? "Restore test passed" : "Restore test failed"); })}
              className="h-9 rounded-full border border-white/15 px-3 text-xs" data-testid={`backup-restore-test-${f.file}`}>{busy === f.file ? "Testing…" : "Restore test"}</button>
            <button type="button" onClick={() => download(f.file).catch((err) => toast.error(formatApiError(err)))} className="h-9 rounded-full border border-white/15 px-3 text-xs" data-testid={`backup-download-${f.file}`}>Download (encrypted)</button>
          </li>
        ))}
      </ul>
      {report && <pre className="overflow-x-auto rounded-2xl border border-white/10 bg-black/40 p-4 text-xs text-stone-300" data-testid="backup-restore-report">{JSON.stringify(report.collections, null, 1)}</pre>}
    </div>
  );
};
